import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchComments, createComment, deleteComment, clearComments } from '../store/slices/commentSlice';

const CommentSection = ({ entityType, entityId }) => {
  const dispatch = useDispatch();
  const { comments, loading } = useSelector((state) => state.comments);
  const { user } = useSelector((state) => state.auth);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (entityType && entityId) {
      dispatch(fetchComments({ entityType, entityId }));
    }

    return () => {
      dispatch(clearComments());
    };
  }, [dispatch, entityType, entityId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await dispatch(createComment({
        entity_type: entityType,
        entity_id: entityId,
        comment: newComment
      })).unwrap();
      setNewComment('');
    } catch (error) {
      console.error('Failed to create comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await dispatch(deleteComment(commentId)).unwrap();
      } catch (error) {
        console.error('Failed to delete comment:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="card mt-3">
      <div className="card-body">
        <h3 className="fs-6 fw-semibold mb-3">Comments</h3>

        <form onSubmit={handleSubmit} className="mb-4">
          <textarea
            className="form-control mb-2"
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={3}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !newComment.trim()}
          >
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </form>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {comments.length === 0 ? (
              <p className="text-muted text-center py-4 mb-0">No comments yet. Be the first to comment!</p>
            ) : (
              comments.map((comment) => (
                <div key={comment._id} className="border rounded-2 p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong className="d-block small">{comment.commented_by?.name}</strong>
                      <span className="text-muted" style={{ fontSize: '12px' }}>
                        {comment.commented_by?.employee_code} &bull; {formatDate(comment.created_at)}
                      </span>
                    </div>
                    {(user?.role === 'admin' || user?._id === comment.commented_by?._id) && (
                      <button
                        onClick={() => handleDelete(comment._id)}
                        className="btn btn-link btn-sm text-danger p-0"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {comment.comment}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
